import { useCallback } from 'react';
import { useTourGuide } from './useTourGuide';
import { useTourDemoData } from './useTourDemoData';
import { useStopTourOnNavigation } from './useStopTourOnNavigation';
import { useGlobalTourConductor } from './useGlobalTourConductor';
import { tourConfigs } from '@components/tours/config';
import type { TourContextValue } from '@components/tours/types';

export const useManageTourProvider = (): TourContextValue => {
  const { startTour: startTourGuide, stopTour: stopTourGuide, isActive, currentTourId } = useTourGuide();
  const { cleanupDemoData, injectDemoData } = useTourDemoData();

  const stopTour = useCallback(() => {
    cleanupDemoData();
    stopTourGuide();
  }, [cleanupDemoData, stopTourGuide]);

  // Initialize global tour conductor
  const {
    startGlobalTour,
    stopGlobalTour,
    isGlobalTourActive,
    globalTourProgress,
    globalTourPhase,
  } = useGlobalTourConductor({
    startLocalTour: startTourGuide,
    stopLocalTour: stopTour,
    isLocalTourActive: isActive,
  });

  // Pass isGlobalTourActive to bypass route-change tour stopping during global tours
  useStopTourOnNavigation(isActive, stopTour, isGlobalTourActive);

  const startTour = useCallback(
    async (tourId: string) => {
      // Mutual exclusion: don't start local tour if global tour is active
      if (isGlobalTourActive) {
        console.warn('Cannot start local tour while global tour is active');
        return;
      }

      const config = tourConfigs[tourId];
      if (!config) {
        console.warn(`Tour config not found: ${tourId}`);
        return;
      }

      await injectDemoData(tourId);
      startTourGuide(config, cleanupDemoData);
    },
    [startTourGuide, injectDemoData, cleanupDemoData, isGlobalTourActive]
  );

  return {
    startTour,
    stopTour,
    isActive,
    currentTourId,
    startGlobalTour,
    stopGlobalTour,
    isGlobalTourActive,
    globalTourProgress,
    globalTourPhase,
  };
};
